/**
 * AffiliateImportPreviewDialog — preview premium e seguro de uma importação de
 * afiliados. Reusa ImportPreviewShell + componentes compartilhados.
 * Dados via RPC `get_affiliate_import_preview` (limite 50, e-mail/telefone/documento mascarados).
 *
 * RELEASE:
 *   2026-05-17 v1.0.0 - Versão inicial.
 */
import { useMemo } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  UserPlus,
  Users,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Inbox,
} from 'lucide-react';

import { useAffiliateImportPreview } from '@/hooks/useAffiliateImportPreview';
import { ImportPreviewShell } from '@/components/imports/shared/ImportPreviewShell';
import { ImportPreviewMetricCard } from '@/components/imports/shared/ImportPreviewMetricCard';
import { ImportPreviewErrorTooltip } from '@/components/imports/shared/ImportPreviewErrorTooltip';

// Mapa: affiliate_import_item_status (pending|valid|invalid|duplicate|existing|processing|succeeded|failed|skipped)
const STATUS_DOT: Record<string, string> = {
  pending: 'bg-muted-foreground',
  valid: 'bg-blue-500',
  invalid: 'bg-amber-500',
  duplicate: 'bg-amber-500',
  existing: 'bg-cyan-500',
  processing: 'bg-blue-500',
  succeeded: 'bg-emerald-500',
  failed: 'bg-red-500',
  skipped: 'bg-muted-foreground',
};

const STATUS_LABEL: Record<string, string> = {
  pending: 'Pendente',
  valid: 'Válido',
  invalid: 'Inválido',
  duplicate: 'Duplicado',
  existing: 'Já existia',
  processing: 'Processando',
  succeeded: 'Criado',
  failed: 'Falha',
  skipped: 'Ignorado',
};

export interface AffiliateImportJobSummary {
  id: string;
  name: string;
  created_at?: string;
  total_rows?: number;
  succeeded_count?: number;
  existing_count?: number;
  invalid_count?: number;
  duplicate_count?: number;
  failed_count?: number;
}

interface AffiliateImportPreviewDialogProps {
  job: AffiliateImportJobSummary;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function AffiliateImportPreviewDialog({
  job,
  open,
  onOpenChange,
}: AffiliateImportPreviewDialogProps) {
  const { data: items, isLoading } = useAffiliateImportPreview(job.id, open);

  const invalidTotal = useMemo(
    () => (job.invalid_count ?? 0) + (job.duplicate_count ?? 0),
    [job],
  );

  return (
    <ImportPreviewShell
      open={open}
      onOpenChange={onOpenChange}
      headerIcon={<UserPlus className="h-4 w-4" />}
      title={job.name}
      createdAt={job.created_at}
      metrics={
        <>
          <ImportPreviewMetricCard
            title="Total"
            value={job.total_rows ?? 0}
            icon={<Users className="h-4 w-4" />}
            variant="primary"
          />
          <ImportPreviewMetricCard
            title="Criados"
            value={job.succeeded_count ?? 0}
            icon={<CheckCircle2 className="h-4 w-4" />}
            variant="success"
          />
          <ImportPreviewMetricCard
            title="Existentes"
            value={job.existing_count ?? 0}
            icon={<Users className="h-4 w-4" />}
            variant="info"
          />
          <ImportPreviewMetricCard
            title="Falhas"
            value={(job.failed_count ?? 0) + invalidTotal}
            icon={<XCircle className="h-4 w-4" />}
            variant="danger"
          />
        </>
      }
    >
      <div className="space-y-2">
        <h3 className="text-sm font-semibold">Amostra de afiliados importados</h3>

        {isLoading ? (
          <div className="rounded-md border overflow-hidden">
            <div className="space-y-px">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-10 w-full rounded-none" />
              ))}
            </div>
          </div>
        ) : !items || items.length === 0 ? (
          <div className="rounded-md border border-dashed py-10 flex flex-col items-center justify-center gap-2 text-muted-foreground">
            <Inbox className="h-8 w-8 opacity-40" />
            <p className="text-sm">Nenhum item disponível para preview.</p>
          </div>
        ) : (
          <div className="rounded-md border overflow-hidden">
            <div className="max-h-[420px] overflow-auto">
              <Table>
                <TableHeader className="sticky top-0 z-10 bg-muted/60 backdrop-blur supports-[backdrop-filter]:bg-muted/50">
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="h-9 w-10 text-xs">#</TableHead>
                    <TableHead className="h-9 text-xs">Nome</TableHead>
                    <TableHead className="h-9 text-xs">E-mail</TableHead>
                    <TableHead className="h-9 text-xs">Telefone</TableHead>
                    <TableHead className="h-9 text-xs">Documento</TableHead>
                    <TableHead className="h-9 text-xs">Programa</TableHead>
                    <TableHead className="h-9 text-xs">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((it) => (
                    <TableRow key={it.id} className="text-xs">
                      <TableCell className="py-2 text-muted-foreground tabular-nums">
                        {it.row_index}
                      </TableCell>
                      <TableCell className="py-2 font-medium max-w-[160px] truncate">
                        {it.name || <span className="text-muted-foreground/60">—</span>}
                      </TableCell>
                      <TableCell className="py-2 text-muted-foreground max-w-[180px] truncate">
                        {it.email_masked || <span className="text-muted-foreground/60">—</span>}
                      </TableCell>
                      <TableCell className="py-2 font-mono tabular-nums whitespace-nowrap">
                        {it.phone_masked || <span className="text-muted-foreground/60">—</span>}
                      </TableCell>
                      <TableCell className="py-2 font-mono tabular-nums whitespace-nowrap">
                        {it.document_masked || (
                          <span className="text-muted-foreground/60">—</span>
                        )}
                      </TableCell>
                      <TableCell className="py-2 text-muted-foreground max-w-[160px] truncate">
                        {it.program_name || (
                          <span className="text-muted-foreground/60">—</span>
                        )}
                      </TableCell>
                      <TableCell className="py-2">
                        <ImportPreviewErrorTooltip
                          colorClass={STATUS_DOT[it.status] ?? 'bg-muted-foreground'}
                          label={STATUS_LABEL[it.status] ?? it.status}
                          errorMessage={it.error_message}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </div>
    </ImportPreviewShell>
  );
}
